import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Copy,
  Trash2,
  CheckCircle2,
  Clock,
  Award,
  GraduationCap,
  FileText,
  Edit,
} from 'lucide-react';
import { useSkillTests } from '../../hooks/useSkillTests';
import type { SkillTest } from '../../types/skillTests';

const getStatusBadgeClass = (status: SkillTest['status']) => {
  if (status === 'active') {
    return 'bg-green-500/20 text-green-400 border-green-500/50';
  }
  if (status === 'draft') {
    return 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50';
  }
  return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
};

export function SkillTestManager() {
  const { tests, toggleStatus, duplicateTest, deleteTest, restoreTest } = useSkillTests();

  const handleToggleStatus = (id: number) => {
    const existing = tests.find((test) => test.id === id);
    if (!existing) {
      toast.error('Unable to update test status.');
      return;
    }
    const updated = toggleStatus(id);
    if (!updated) {
      toast.error('Unable to update test status.');
      return;
    }

    toast.success(
      updated.status === 'active'
        ? `${updated.title} is now active`
        : `${updated.title} saved as draft`,
      {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => {
            const reverted = toggleStatus(id);
            if (reverted) {
              toast.info(`${existing.title} status reverted to ${existing.status}`, {
                duration: 3000,
              });
            }
          },
        },
      },
    );
  };

  const handleDuplicateTest = async (id: number) => {
    const duplicated = duplicateTest(id);
    if (duplicated) {
      toast.success('Skill test duplicated', {
        description: `${duplicated.title} added to drafts.`,
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: async () => {
            const removed = await deleteTest(duplicated.id);
            if (removed) {
              toast.info('Duplicate removed.', { duration: 3000 });
            }
          },
        },
      });
    } else {
      toast.error('Unable to duplicate skill test.');
    }
  };

  const handleDeleteTest = async (id: number) => {
    const deleted = await deleteTest(id);
    if (deleted) {
      toast.success('Skill test deleted', {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => {
            restoreTest(deleted);
            toast.info('Skill test restored.', { duration: 3000 });
          },
        },
      });
    } else {
      toast.error('Skill test no longer exists.');
    }
  };

  const totalActive = tests.filter((test) => test.status === 'active').length;
  const totalUses = tests.reduce((sum, test) => sum + test.timesUsed, 0);
  const averagePassingScore = tests.length
    ? Math.round(tests.reduce((sum, test) => sum + test.passingScore, 0) / tests.length)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="mb-2 text-gold-gradient">
            Skill Tests & Pre-Interview Questions
          </h2>
          <p className="text-gray-400">Create and manage assessments before inviting candidates.</p>
        </div>
        <Button
          asChild
          className="bg-gold-gradient hover:brightness-110 text-black"
        >
          <Link to="/employer/tests/new">
            <Plus className="mr-2 h-4 w-4" />
            Create New Test
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="border-white/10 bg-white/[0.03]">
          <CardContent className="flex items-center gap-3 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gold-gradient text-black">
              <FileText className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total tests</p>
              <p className="text-white text-lg">{tests.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardContent className="flex items-center gap-3 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/20 text-green-300">
              <CheckCircle2 className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-gray-400">Active tests</p>
              <p className="text-white text-lg">{totalActive}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardContent className="flex items-center gap-3 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/20 text-blue-300">
              <GraduationCap className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total uses</p>
              <p className="text-white text-lg">{totalUses}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardContent className="flex items-center gap-3 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/20 text-purple-300">
              <Award className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="text-sm text-gray-400">Avg. passing score</p>
              <p className="text-white text-lg">{averagePassingScore}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {tests.map((test) => {
          const totalPoints = test.questions.reduce((sum, question) => sum + question.points, 0);
          return (
            <Card
              key={test.id}
              className="border-white/10 bg-white/[0.03] transition-all hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)]"
            >
              <CardContent className="flex flex-col gap-6 p-6 md:flex-row md:items-start md:justify-between">
                <div className="flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <CardTitle className="text-xl text-white">{test.title}</CardTitle>
                    <Badge className={getStatusBadgeClass(test.status)}>{test.status}</Badge>
                    <Badge variant="outline" className="border-white/20 text-gray-300">
                      {test.category}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm text-gray-400">{test.description}</CardDescription>
                  <div className="grid gap-4 text-sm text-gray-300 md:grid-cols-5">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#ba9036]" aria-hidden />
                      <span>{test.questions.length} questions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-400" aria-hidden />
                      <span>{test.duration} minutes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-purple-400" aria-hidden />
                      <span>{totalPoints} total points</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-400" aria-hidden />
                      <span>{test.passingScore}% to pass</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-[#ba9036]" aria-hidden />
                      <span>Used {test.timesUsed} times</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:w-40">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Link to={`/employer/tests/${test.id}/edit`}>
                      <Edit className="mr-1 h-4 w-4" aria-hidden />
                      Edit
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(test.id)}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    {test.status === 'active' ? 'Set draft' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDuplicateTest(test.id)}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Copy className="mr-1 h-4 w-4" aria-hidden />
                    Duplicate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteTest(test.id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="mr-1 h-4 w-4" aria-hidden />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {tests.length === 0 && (
        <Card className="border-white/10 bg-white/[0.02]">
          <CardContent className="p-12 text-center">
            <FileText className="mx-auto mb-4 h-16 w-16 text-gray-600" aria-hidden />
            <h3 className="mb-2 text-white">No skill tests yet</h3>
            <p className="mb-6 text-sm text-gray-400">
              Draft an assessment to screen for core competencies before inviting candidates to interview.
            </p>
            <Button
              asChild
              className="bg-gold-gradient hover:brightness-110 text-black"
            >
              <Link to="/employer/tests/new">
                <Plus className="mr-2 h-4 w-4" />
                Create your first test
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
